import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle2, ShieldCheck, Wallet, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePaymentMode } from '../contexts/PaymentModeContext';

/**
 * Custom event name used to reopen the modal without touching localStorage.
 * Dispatched by `openHostOnboardingHelper()` or by clicking `HostOnboardingHelperLink`.
 */
const OPEN_EVENT = 'hh:open-host-onboarding-helper';

export const openHostOnboardingHelper = () => {
  try {
    window.dispatchEvent(new Event(OPEN_EVENT));
  } catch (_) { /* SSR / no-window safety */ }
};

/**
 * HostOnboardingHelperModal (Phase B, host_direct only)
 *
 * One-time onboarding modal shown on Host Dashboard that explains the 3 core
 * direct-payment actions a host needs to know:
 *   1. Mark Payment Received
 *   2. Record Bond
 *   3. How you get paid (directly)
 *
 * Persistence: localStorage per user (`hh_host_onboarding_helper_v1_<user_id>`).
 * No backend changes required. Renders nothing unless PAYMENT_MODE=host_direct.
 */

const STORAGE_PREFIX = 'hh_host_onboarding_helper_v1_';

const Step = ({ icon: Icon, title, description, testId }) => (
  <div className="flex gap-3 items-start" data-testid={testId}>
    <div className="w-9 h-9 rounded-lg bg-[#004B8D]/10 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#004B8D]" />
    </div>
    <div className="flex-1">
      <h4 className="font-semibold text-slate-800 text-sm">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed mt-0.5">{description}</p>
    </div>
  </div>
);

const HostOnboardingHelperModal = () => {
  const { user, isHost } = useAuth();
  const { isHostDirect, loaded } = usePaymentMode();
  const [open, setOpen] = useState(false);

  const userId = user?.user_id || user?.id;
  const isHostUser = typeof isHost === 'function' ? isHost() : false;
  const storageKey = userId ? `${STORAGE_PREFIX}${userId}` : null;

  useEffect(() => {
    if (!loaded || !isHostDirect) return;
    if (!userId || !isHostUser) return;
    if (!storageKey) return;

    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) {
        // Slight delay so dashboard has time to paint first
        const t = setTimeout(() => setOpen(true), 400);
        return () => clearTimeout(t);
      }
    } catch (_) {
      // localStorage disabled — do nothing
    }
  }, [loaded, isHostDirect, userId, isHostUser, storageKey]);

  // Manual reopen listener — does NOT touch the localStorage flag.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  const handleDismiss = () => {
    try {
      if (storageKey) localStorage.setItem(storageKey, new Date().toISOString());
    } catch (_) {
      // ignore
    }
    setOpen(false);
  };

  if (!loaded || !isHostDirect) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent
        className="max-w-md sm:max-w-lg"
        data-testid="host-onboarding-helper-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            Welcome! 3 things to know as a direct-payment host
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            HireHub runs in direct-payment mode. You arrange payments with the renter
            yourself — we handle the booking workflow, evidence, and protection.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Step
            icon={CheckCircle2}
            testId="host-onboarding-helper-step-mark-payment"
            title="1. Mark Payment Received"
            description="When a renter sends the rental amount (PayID, bank transfer, cash), open the booking and tap 'Mark Payment Received' to move it to Paid."
          />
          <Step
            icon={ShieldCheck}
            testId="host-onboarding-helper-step-record-bond"
            title="2. Record Bond"
            description="Collect the security bond directly from the renter before trip start. Use 'Record Bond' on the booking to log the amount and method — required to start the trip."
          />
          <Step
            icon={Wallet}
            testId="host-onboarding-helper-step-how-you-get-paid"
            title="3. How you get paid"
            description="Renters pay you directly. Add your PayID or bank details under Account → How you get paid so renters know exactly where to send funds."
          />
        </div>

        <DialogFooter className="sm:justify-end">
          <Button
            onClick={handleDismiss}
            className="bg-[#004B8D] hover:bg-[#003a6f] text-white"
            data-testid="host-onboarding-helper-dismiss-btn"
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HostOnboardingHelperModal;

/**
 * Small text link that reopens the Quick Start Guide modal.
 * Renders nothing when `PAYMENT_MODE=platform` (the guide is host_direct-specific).
 * Does NOT mutate the localStorage "seen" flag — reopening is free and repeatable.
 */
export const HostOnboardingHelperLink = ({ className = '' }) => {
  const { isHostDirect, loaded } = usePaymentMode();
  if (!loaded || !isHostDirect) return null;

  return (
    <button
      type="button"
      onClick={openHostOnboardingHelper}
      className={`inline-flex items-center gap-1.5 text-sm text-[#004B8D] hover:text-[#003a6f] hover:underline underline-offset-2 transition-colors ${className}`}
      data-testid="host-onboarding-helper-reopen-link"
    >
      <HelpCircle className="w-3.5 h-3.5" />
      View Quick Start Guide
    </button>
  );
};
